import { NodeViewWrapper } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { reactionBody } from './reaction-formula-extension';
import { chemistryReady, prepareChemistry } from './structure-render';

import type { ReactionFormulaOptions } from './reaction-formula-extension';
import type { NodeViewProps } from '@tiptap/core';

export function ReactionFormulaView({ node, extension, updateAttributes }: NodeViewProps) {
  const { t } = useTranslation();
  const { onEditRequest } = (extension as unknown as { options: ReactionFormulaOptions }).options;
  const formula = String(node.attrs.formula);
  const note = String(node.attrs.note ?? '');
  const feynman = String(node.attrs.feynman ?? '');
  /* The SAME body the student will see — drawn structures, the Feynman diagram, the enthalpy
     beside it, all of it. This view used to typeset the formula itself, which is how a flagged
     reaction drew on the page and stayed an equation in the editor. */
  /*
   * ASK FOR THE DRAWING LIBRARY when this reaction needs one.
   *
   * It is ~330K and loads on demand, and until now the only things that asked were the picker
   * and the builder. So a task REOPENED with molecules already in it showed their names and
   * nothing else, forever, unless the teacher happened to open the palette — the drawing was
   * never lost, it was never requested. `pending` says this reaction wants it.
   */
  const [ready, setReady] = useState(chemistryReady());
  const { html, error, label, pending } = reactionBody(formula, note, feynman);
  useEffect(() => {
    if (!pending || ready) return;
    let alive = true;
    void prepareChemistry().then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [pending, ready]);

  function handleEdit() {
    onEditRequest(formula, note, feynman, (next) => updateAttributes(next));
  }

  return (
    <NodeViewWrapper
      as='span'
      className='reaction-formula cursor-pointer'
      style={{ whiteSpace: 'nowrap' }}
      onDragStart={(e: React.DragEvent) => e.preventDefault()}
      onClick={handleEdit}
      tabIndex={0}
      aria-label={[label || formula, t('blocks.toolbar.reactionFormulaLabel')].filter(Boolean).join(' — ')}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(); }
      }}
    >
      <span style={{ display: 'block', overflowX: 'auto', overflowY: 'hidden', whiteSpace: 'nowrap', paddingRight: '0.5em' }}>
        {html ? (
          <span dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <span className='text-xs' style={{ color: 'var(--color-ws-error-fg)' }}>
            {error ?? formula}
          </span>
        )}
      </span>
    </NodeViewWrapper>
  );
}
